import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENABLE_ROLE_SWITCHER = process.env.REACT_APP_ENABLE_ROLE_SWITCHER === 'true';

const AuthContext = createContext(null);

const AVAILABLE_ROLES = [
  { value: 'ADMIN', label: 'Admin', description: 'Full access + Analytics' },
  { value: 'COMMAND_CENTER', label: 'Command Center Agent', description: 'Dispatch & Incident Management' },
  { value: 'FIRST_RESPONDER', label: '1st Responder', description: 'Non-violent incidents (L1/L2)' },
  { value: 'SECOND_RESPONDER', label: '2nd Responder', description: 'Serious incidents (L3/L4)' },
  { value: 'REQUESTER', label: 'Requester (Driver/Rider)', description: 'SOS & Status View' },
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [activeRole, setActiveRole] = useState(() => ENABLE_ROLE_SWITCHER ? localStorage.getItem('active_role') || null : null);

  const checkAuth = useCallback(async () => {
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('auth_token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Lightweight alternative to checkAuth() for call sites that already have a
  // fresh user object in hand (e.g. a PATCH response) and just need to update
  // local state -- avoids a redundant GET /auth/me round trip. Merges onto the
  // existing user rather than replacing outright, in case the caller only has
  // a partial payload.
  const updateUser = useCallback((userData) => {
    if (!userData) return;
    setUser(prev => (prev ? { ...prev, ...userData } : userData));
  }, []);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('auth_token', data.token);
    localStorage.removeItem('active_role');
    setToken(data.token);
    setUser(data.user);
    setActiveRole(null);
    return data.user;
  };

  const register = async (name, email, password, role = 'COMMAND_CENTER', phone = '') => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, email, password, role, phone: phone || null })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    localStorage.setItem('auth_token', data.token);
    localStorage.removeItem('active_role');
    setToken(data.token);
    setUser(data.user);
    setActiveRole(null);
    return data.user;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: token
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        : { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Password change failed');
    }

    return response.json();
  };

  const logout = async () => {
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
          await fetch(`${API_URL}/api/push/subscriptions`, {
            method: 'DELETE',
            credentials: 'include',
            headers: token ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } : { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endpoint: subscription.endpoint })
          }).catch(() => null);
          await subscription.unsubscribe().catch(() => null);
        }
      }
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('active_role');
      setToken(null);
      setUser(null);
      setActiveRole(null);
    }
  };

  const switchRole = (role) => {
    if (!ENABLE_ROLE_SWITCHER) return;
    setActiveRole(role);
    localStorage.setItem('active_role', role);
  };

  // Get the current active role (either switched role or user's actual role)
  const currentRole = (ENABLE_ROLE_SWITCHER && activeRole) || user?.role || 'COMMAND_CENTER';

  const value = {
    user,
    loading,
    token,
    login,
    register,
    changePassword,
    logout,
    checkAuth,
    updateUser,
    isAuthenticated: !!user,
    // Role switching
    activeRole: currentRole,
    switchRole,
    roleSwitcherEnabled: ENABLE_ROLE_SWITCHER,
    availableRoles: AVAILABLE_ROLES,
    isAdmin: currentRole === 'ADMIN',
    isCommandCenter: currentRole === 'COMMAND_CENTER' || currentRole === 'ADMIN',
    isResponder: currentRole === 'FIRST_RESPONDER' || currentRole === 'SECOND_RESPONDER',
    isRequester: currentRole === 'REQUESTER',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
